import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { sageJobSiteService } from "../../services/sageJobSiteService"
import { SageJobSite } from "../../Interfaces"
import { useSelectedCompany } from "../../context/companyProvider"
import { formatCompanyContext } from "../../utils/companyAccess"
import { displayJobSiteCode } from "../../utils/sageCodes"
import { SageApiError } from "../../utils/sageApiError"
import { t } from "../../i18n"
import CwPageHeader from "../construction/CwPageHeader"
import CwDataTable from "../construction/CwDataTable"
import SpreadsheetImportZone from "../construction/SpreadsheetImportZone"
import CompanyChangeAction from "../construction/CompanyChangeAction"
import CwAddButton from "../construction/CwAddButton"
import CwRowActions from "../construction/CwRowActions"
import CwConfirmDialog from "../construction/CwConfirmDialog"
import JobSiteFormModal from "../construction/JobSiteFormModal"
import { CircularProgress } from "@mui/material"

function belongsToCompany(site: SageJobSite, companyId?: number) {
  if (!companyId) return false
  // Trust the company-scoped API query when the row omits a company id.
  // Drop only rows that clearly belong to a different company.
  if (site.managementCompanyId == null) return true
  return site.managementCompanyId === companyId
}

export default function JobSitesTable() {
  const { selectedCompany, isAdmin, canManageSageMaster } = useSelectedCompany()
  const companyId = selectedCompany?.id
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<SageJobSite | null>(null)
  const [deleting, setDeleting] = useState<SageJobSite | null>(null)
  const [deleteError, setDeleteError] = useState("")
  const [deleteSubmitting, setDeleteSubmitting] = useState(false)

  const { data: response, isLoading, error } = useQuery(
    ["sage-job-sites", companyId],
    () => sageJobSiteService.getAll(500, 0, companyId ? { managementCompanyId: companyId } : undefined),
    { cacheTime: 5000, suspense: false, useErrorBoundary: false, enabled: !!companyId },
  )

  const rows = (response?.data || []).filter((site) => belongsToCompany(site, companyId))

  const refetch = () => {
    queryClient.invalidateQueries(["sage-job-sites", companyId])
  }

  const handleDelete = async () => {
    if (!companyId || !deleting) return
    setDeleteSubmitting(true)
    setDeleteError("")
    try {
      await sageJobSiteService.deleteJobSite(companyId, deleting.id)
      setDeleting(null)
      refetch()
    } catch (deleteErr) {
      setDeleteError(deleteErr instanceof SageApiError && deleteErr.message
        ? deleteErr.message
        : t("jobSites.loadFailed"))
    } finally {
      setDeleteSubmitting(false)
    }
  }

  return (
    <div>
      <CwPageHeader
        title={t("jobSites.title")}
        subtitle={selectedCompany ? formatCompanyContext(selectedCompany) : undefined}
        action={(
          <div className="flex flex-wrap items-center gap-3">
            {canManageSageMaster && companyId ? (
              <CwAddButton
                label={t("jobSites.add")}
                onClick={() => {
                  setEditing(null)
                  setFormOpen(true)
                }}
              />
            ) : null}
            <CompanyChangeAction />
          </div>
        )}
      />
      {isAdmin ? (
        <SpreadsheetImportZone
          kind="job-sites"
          managementCompanyId={companyId}
          onImported={refetch}
        />
      ) : null}
      {isLoading ? (
        <div className="bg-white rounded-2xl shadow-lg h-48 flex items-center justify-center">
          <CircularProgress />
        </div>
      ) : error ? (
        <p className="bg-white rounded-2xl shadow-lg px-5 py-8 text-center text-red-600 font-source">
          {t("jobSites.loadFailed")}
        </p>
      ) : (
        <CwDataTable
          columns={[
            {
              key: "name",
              label: t("jobSites.columnName"),
              render: (row) => (
                <Link
                  to={`/app/locations/${row.id}`}
                  className="font-semibold text-primary hover:underline"
                  onClick={(event) => event.stopPropagation()}
                >
                  {row.name}
                </Link>
              ),
            },
            { key: "code", label: t("jobSites.columnCode"), render: (row) => displayJobSiteCode(row) },
            ...(canManageSageMaster ? [{
              key: "actions",
              label: t("common.actions"),
              render: (row: SageJobSite) => (
                <CwRowActions
                  onEdit={() => {
                    setEditing(row)
                    setFormOpen(true)
                  }}
                  onDelete={() => {
                    setDeleteError("")
                    setDeleting(row)
                  }}
                />
              ),
            }] : []),
          ]}
          rows={rows}
          empty={t("jobSites.empty")}
          onRowClick={(row) => navigate(`/app/locations/${row.id}`)}
        />
      )}
      {companyId ? (
        <JobSiteFormModal
          isOpen={formOpen}
          companyId={companyId}
          jobSite={editing}
          onClose={() => {
            setFormOpen(false)
            setEditing(null)
          }}
          onSaved={refetch}
        />
      ) : null}
      <CwConfirmDialog
        isOpen={Boolean(deleting)}
        title={t("jobSites.deleteTitle")}
        message={t("jobSites.deleteConfirm")}
        error={deleteError}
        submitting={deleteSubmitting}
        onConfirm={() => void handleDelete()}
        onClose={() => {
          if (!deleteSubmitting) {
            setDeleting(null)
            setDeleteError("")
          }
        }}
      />
    </div>
  )
}
